// @ts-strict-ignore
/**
 * A "multi-block" manipulates multiple query blocks together, for instance
 * multiple filters.
 */
import {Component} from 'panel';

import {QueryReason} from 'common/report/util/query-tracking/types';
import {removeByValue, replaceByIndex} from 'common/util/array';
import {emitTrackEvent, preventTracking, propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {toSentenceCase} from 'common/util/string';
import {QueryBlockTheme, SelectorType} from 'common/query-builder/query-block/constants';
import {getSegmentType} from 'common/query-builder/query-block/segment-menu/types';
import {TypecastPillAction} from 'common/query-builder/query-block/properties-menu/types';
import {ResourceType} from 'common/report/constants';
import {filterAttributes} from 'common/util';

import {QueryBlockParams} from '../query-block/types';
import {template} from './template';
import css from './index.inline.styl';

interface QueryMultiBlockState {
  addingBlock: boolean;
  prefixRenderer: any;
  // this has the same effect as this.state.selectionObject in query-builder. Read there for context
  stagingSelected: Array<Record<string, any>>;
}

export class QueryMultiBlock extends Component<QueryMultiBlockState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'adding-disabled': `boolean`,
      'allow-cohort-filter': `boolean`,
      'allow-typecast-pill-action': {
        type: `string`,
        description: `allows showing a pill labeled typecast next to eligible properties`,
        enum: Object.values(TypecastPillAction),
        default: null,
      },
      'all-data-groups': `boolean`,
      'block-prefix': `string`,
      'button-icon': {
        type: `string`,
        default: `plus`,
      },
      'enable-enhanced-list-filters': `boolean`,
      'button-open': `boolean`,
      'button-text': {type: `string`, default: null},
      'button-selector-type': {type: `string`, default: SelectorType.Button, enum: Object.values(SelectorType)},
      'button-selector-theme': {
        type: `string`,
        default: QueryBlockTheme.Secondary,
        enum: Object.values(QueryBlockTheme),
      },
      'data-group-id': `string`,
      'disable-all-scd-props': `boolean`,
      'enable-workspace-filter-restrictions': `boolean`,
      'event-names': {type: `json`, default: null},
      'exclude-custom-events': `boolean`,
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'exclude-lookup-table-properties': {
        type: `boolean`,
        description: `whether to hide user uploaded properties from a lookup table`,
      },
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
      },
      'prevent-close': {
        type: `boolean`,
        description: `Prevent mp-drop-menu from closing when another one opens, e.g. on boards prevent card tooltips closing the global filter selection`,
      },
      'show-borrowed-properties': `boolean`,
      tooltip: {
        type: `json`,
        description: `tooltip to render within the "add" query block`,
      },
      'first-block-prefix': `string`,
      layout: {type: `string`, enum: [`row`, `column`], default: `row`},
      limit: {type: `number`, default: null},
      'other-group-by-options': {type: `json`, default: []},
      'property-combine-mode': {type: `string`, default: null},
      'project-has-cohorts': `boolean`,
      'hide-events': `boolean`,
      'project-has-people': `boolean`,
      'project-id': {type: `number`, default: null},
      'hide-edit-controls': {
        type: `boolean`,
        description: `show controls that popup modals to edit entities like custom events and properties`,
      },
      selected: {type: `json`, default: []},
      'show-special-dataset-property': `boolean`,
      'selector-type': `string`,
      theme: {type: `string`, default: QueryBlockTheme.Primary, enum: Object.values(QueryBlockTheme)},
      type: {type: `string`, default: null},
      'user-id': `number`,
      'support-b2b-cohorts': `boolean`,
      'inline-cohorts-override': `json`,
      'track-name': {
        type: `string`,
        description: `If specified, dispatches '[mp-query-builder] <event>' bubbled events that can be listened for tracking with "Report name" as a property`,
      },
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      slowThreshold: 215, // p75 from https://mixpanel.com/s/2kFNfR

      defaultState: {
        addingBlock: false,
        prefixRenderer: null,
        stagingSelected: [],
      },

      helpers: {
        preventTracking,
        propagateEvent: (ev) => propagateEvent(this.app, ev),
        buttonText: () => this.attr(`button-text`) || toSentenceCase(this.attr(`type`)) || ``,
        canAddMore: () => !Number.isInteger(this.attr(`limit`)) || this.attr(`limit`) > this.attr(`selected`).length,
        removeSelection: (selectionValue: Obj) => this.updateItems(`remove`, {selectionValue}),
        setAddingBlock: (addingBlock) => this.update({addingBlock}),
        onAddItemBlockClose: () => {
          this.helpers.setAddingBlock(false);
          this.dispatchEvent(new CustomEvent(`close`));
        },

        modifySelection: (index, selection) => {
          const {trackingProps, queryReason} = selection;
          const item = this.extractItemFromEvent({detail: selection});
          this.updateItems(`edit`, {item, index, trackingProps, queryReason});
        },

        selectionIsCohort: (selection) => getSegmentType(selection) === ResourceType.Cohort,

        commitNewBlock: (detail) => {
          const {trackingProps, queryReason} = detail;
          const item = this.extractItemFromEvent({detail});

          this.helpers.setAddingBlock(false);
          this.updateItems(`add`, {item, trackingProps, queryReason});
        },

        getQueryBlockProps: (): QueryBlockParams => {
          return filterAttributes({
            type: this.attr(`type`),
            allowTypecastPillAction: this.attr(`allow-typecast-pill-action`),
            allDataGroups: this.attr(`all-data-groups`),
            dataGroupId: this.attr(`data-group-id`),
            enableEnhancedListFilters: this.attr(`enable-enhanced-list-filters`),
            enableWorkspaceFilterRestrictions: this.attr(`enable-workspace-filter-restrictions`),
            eventNames: this.attr(`event-names`),
            excludeCustomEvents: this.attr(`exclude-custom-events`),
            excludeDimensionProperties: this.attr(`exclude-dimension-properties`),
            excludeLookupTableProperties: this.attr(`exclude-lookup-table-properties`),
            includeCustomProperties: this.attr(`include-custom-properties`),
            showBorrowedProperties: this.attr(`show-borrowed-properties`),
            headerText: ``,
            otherGroupByOptions: this.attr(`other-group-by-options`),
            propertyCombineMode: this.attr(`property-combine-mode`),
            projectHasCohorts: this.attr(`project-has-cohorts`),
            hideEvents: this.attr(`hide-events`),
            projectHasPeople: this.attr(`project-has-people`),
            hideEditControls: this.attr(`hide-edit-controls`),
            showSpecialDatasetProperty: this.attr(`show-special-dataset-property`),
            projectId: this.attr(`project-id`),
            selectorType: this.attr(`selector-type`),
            theme: this.attr(`theme`),
            userId: this.attr(`user-id`),
            supportB2bCohorts: this.attr(`support-b2b-cohorts`),
            inlineCohortsOverride: this.attr(`inline-cohorts-override`),
            trackName: this.attr(`track-name`),
            workspaceId: this.attr(`workspace-id`),
          });
        },
      },
    };
  }

  set prefixRenderer(prefixRenderer) {
    this.update({prefixRenderer});
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `selected`) {
      this.update({
        stagingSelected: this.attr(`selected`),
      });
    }
  }

  private extractItemFromEvent(ev) {
    if (this.attr(`type`) === `cohorts`) {
      return ev.detail.selected;
    }
    return ev.detail.item;
  }

  private updateItems(
    action,
    {
      item,
      index,
      selectionValue,
      trackingProps = {},
      queryReason,
    }: {
      item?: any;
      index?: number;
      newIndex?: number;
      selectionValue?: Obj;
      trackingProps?: Obj;
      queryReason?: QueryReason;
    },
  ) {
    let selections = this.state.stagingSelected || [];
    switch (action) {
      case `add`:
        selections = [...selections, item];
        break;
      case `edit`:
        selections = replaceByIndex(selections, index, item);
        break;
      case `remove`:
        item = selectionValue;
        // note we can't remove by index here. This is because within a frame, due to callbacks, all
        // selections in the array may need to remove themselves. For a 2-array, 'remove 0' and
        // 'remove 1' from children would have 'remove 1' fail due to us having a proper view
        // here due to the buffered selection object but the children underneath having an outdated
        // one as the drawing cycle has not completed yet.
        selections = removeByValue(selections, selectionValue);
        break;
    }
    const {eventName, props} = this.trackingProps(action, item, trackingProps);
    this.update({stagingSelected: selections});
    const detail = {
      items: selections,
      queryReason: queryReason || QueryReason.QbOtherUpdate,
    };
    this.dispatchEvent(new CustomEvent(`update`, {detail}));
    emitTrackEvent(this, {eventName, props, eventPrefix: null, cancelable: true});
  }

  // Generates tracking properties for a given configuration of the component
  private trackingProps(action, item, trackingProps) {
    const menuType = this.attr(`type`);
    let eventName = ``;
    let props = trackingProps || {};

    // TODO delegate this to the child somehow
    switch (menuType) {
      case `breakdown`:
        eventName = `${toSentenceCase(action)} event/property breakdown`;
        props = {
          'Event name': item.event.label,
          'Property data type': item.property.type,
          'Property name': item.property.label,
          'Property raw name': item.property.name,
        };
        break;
      case `properties`:
        eventName = `${toSentenceCase(action)} property`;
        props = {
          'Property data type': item.type,
          'Property name': item.label,
          'Property raw name': item.name,
        };
        break;
      case `filter`:
        eventName = `${toSentenceCase(action)} property filter`;
        break;
      case `segment`:
        eventName = `${toSentenceCase(action)} segment`;
        break;
      case `events`:
        eventName = `${toSentenceCase(action)} event name`;
        props = {
          'Event name': item.label,
          'Event raw name': item.name,
        };
        break;
      default:
        throw new Error(`tracking not defined for mp-query-multi-block configuration`);
    }

    return {eventName, props};
  }
}

export const QueryMultiBlockEl = defineAndMakeJsxFactory(`mp-query-multi-block`, QueryMultiBlock);
export const QueryMultiBlockReact = panel2react(`mp-query-multi-block`, QueryMultiBlock);
